import pdfMake from './setupPdfMake';
import { fetchImageAsDataUrl, fetchApiImageAsDataUrl, scaleImageToFit } from './fetchImageAsDataUrl';

/** Public URL path to dossier logo (served from /public). */
export const DOSSIER_LOGO_PATH = '/images/logo/dsk.png';

/**
 * Optional on `dossierData.personal`:
 * - `children` — `{ no?: number, name: string, birthDate: string }[]`
 * - `childrenCount` — number shown as «Брой деца», defaults to `children.length` when omitted
 */
export const DOSSIER_PERSONAL_DATA_FIELD_KEYS = [
    'fullName',
    'egn',
    'serviceNumber',
    'birthDate',
    'age',
    'birthPlace',
    'birthCountry',
    'nationality',
    'gender',
    'maritalStatus',
    'permanentAddress',
    'permanentCity',
    'permanentPostalCode',
    'permanentCountry',
    'correspondenceAddress',
    'correspondenceCity',
    'correspondencePostalCode',
    'correspondenceCountry',
];

const DOSSIER_PERSONAL_DATA_LABELS = [
    'Име:',
    'ЕГН:',
    'Сл.номер:',
    'Дата на раждане:',
    'Възраст:',
    'Място на раждане:',
    'Държава:',
    'Националност:',
    'Пол:',
    'Семейно положение:',
    'Постоянен адрес:',
    'Град:',
    'Пощ. код:',
    'Държава:',
    'Адрес за кореспонденция:',
    'Град:',
    'Пощ. код:',
    'Държава:',
];

const EMPTY_DOSSIER_PERSONAL_DATA = {
    fullName: '',
    egn: '',
    serviceNumber: '',
    birthDate: '',
    age: '',
    birthPlace: '',
    birthCountry: '',
    nationality: '',
    gender: '',
    maritalStatus: '',
    childrenCount: '0',
    children: [],
    permanentAddress: '',
    permanentCity: '',
    permanentPostalCode: '',
    permanentCountry: '',
    correspondenceAddress: '',
    correspondenceCity: '',
    correspondencePostalCode: '',
    correspondenceCountry: '',
};

export const DOSSIER_CURRENT_POSITION_FIELD_KEYS = [
    'company',
    'management',
    'position',
    'level',
    'startDate',
    'contractType',
    'manager',
    'dailyEmployeeStatus',
    'titularName',
    'deputyName',
    'nextWorkExperiencePercentChangeDate',
];

const DOSSIER_CURRENT_POSITION_LABELS = [
    'Компания:',
    'Управление:',
    'Длъжност:',
    'Ниво:',
    'Дата на постъпване:',
    'Тип на договор:',
    'Ръководител:',
    'Дневен статус на служителя:',
    'Име на титуляр:',
    'Име на заместник:',
    'Дата на следваща промяна на % за трудов стаж:',
];

const EMPTY_DOSSIER_CURRENT_POSITION_DATA = Object.fromEntries(
    DOSSIER_CURRENT_POSITION_FIELD_KEYS.map((key) => [key, ''])
);

/** Keys for each row in `additionalExperience.experiences`. */
export const DOSSIER_ADDITIONAL_EXPERIENCE_ROW_KEYS = [
    'startDate',
    'endDate',
    'position',
    'structuralGroup',
    'level',
];

/** Keys for each row inside «Възнаграждение» grid (under «Допълнителен опит»). */
export const DOSSIER_REMUNERATION_ROW_KEYS = [
    'date',
    'remunerationCode',
    'baseSalary',
    'grossSalary',
    'currency',
    'increasePercent',
    'reason',
];

const DOSSIER_REMUNERATION_HEADER_LABELS = [
    'Дата',
    'Възнаграждение код',
    'Основна заплата',
    'Брутна заплата',
    'Валута',
    '% Повишение',
    'Мотив',
];

export const DOSSIER_ADDITIONAL_EXPERIENCE_FOOTER_KEYS = [
    'bonusRemuneration',
    'coursesCurrentYear',
    'coursesCurrentCompleted',
];

const DOSSIER_ADDITIONAL_EXPERIENCE_HEADER_LABELS = [
    'Начална дата:',
    'Крайна дата:',
    'Длъжност:',
    'Структурна група:',
    'Ниво:',
];

const DOSSIER_ADDITIONAL_EXPERIENCE_FOOTER_LABELS = [
    'Бонус възнаграждение:',
    'Курсове за текущата година:',
    'Курсове текущи / завършени:',
];

const EMPTY_DOSSIER_ADDITIONAL_EXPERIENCE_DATA = {
    experiences: [],
    remuneration: [],
    bonusRemuneration: '',
    coursesCurrentYear: '',
    coursesCurrentCompleted: '',
};

/** Keys for each data row inside «Образование» (nested under «Предишен опит»). */
export const DOSSIER_EDUCATION_ROW_KEYS = [
    'document',
    'degree',
    'specialty',
    'graduationDate',
    'institution',
    'country',
];

const DOSSIER_EDUCATION_HEADER_LABELS = [
    'Документ:',
    'Степен:',
    'Специалност:',
    'Дата на\nзавършване:',
    'Учебно заведение:',
    'Страна:',
];

/** Keys for each row inside «Езици» grid (under «Предишен опит»). */
export const DOSSIER_LANGUAGE_ROW_KEYS = [
    'language',
    'listening',
    'reading',
    'interaction',
    'speaking',
    'writing',
];

const DOSSIER_LANGUAGE_HEADER_LABELS = [
    'Език',
    'слушане',
    'четене',
    'интеракция',
    'говор',
    'писане',
];

const EMPTY_DOSSIER_PREVIOUS_EXPERIENCE_DATA = {
    education: [],
    languages: [],
};

const DOSSIER_TABLE_STYLES = {
    sectionHeaderFontSize: 8,
    cellFontSize: 7,
    labelColWidth: 126,
    sectionHeaderFill: '#dedede',
    labelFill: '#f2f2f2',
    borderColor: '#bdbdbd',
    titleMarginBottom: 6,
    childrenMiniHeaderFill: '#e8e8e8',
    /** Outer document title row (Д О С И Е) */
    documentTitleFontSize: 13,
    documentTitleTopMargin: 12,
    /** Max photo size in header — aspect ratio preserved (not stretched). */
    employeePhotoMaxWidth: 85,
    employeePhotoMaxHeight: 105,
};

function formatDossierScalar(value) {
    return value != null && String(value).trim() !== '' ? String(value) : '—';
}

/**
 * Nested «деца» grid under «Брой деца» (№ / Дете / Дата на).
 * @param {Array<{ no?: number, name: string, birthDate?: string }>} items
 */
function buildChildrenMiniTable(items, s) {
    const fs = Math.max(6, Number(s.cellFontSize) - 1);
    const hdr = s.childrenMiniHeaderFill;
    const line = () => s.borderColor;
    const pad = [2, 1, 2, 1];

    const headerCells = ['№', 'Дете', 'Дата на'].map((text) => ({
        text,
        bold: true,
        fontSize: fs,
        fillColor: hdr,
        margin: pad,
    }));

    const body = (items ?? []).map((child, idx) => {
        const num = child?.no != null ? String(child.no) : String(idx + 1);
        const nm = formatDossierScalar(child?.name);
        const dt = child?.birthDate != null && String(child.birthDate).trim() !== '' ? String(child.birthDate) : '—';
        return [
            { text: num, fontSize: fs, margin: pad },
            { text: nm, fontSize: fs, margin: pad },
            { text: dt, fontSize: fs, margin: pad },
        ];
    });

    return {
        margin: [0, 3, 0, 0],
        table: {
            widths: [24, '*', 52],
            body: [headerCells, ...body],
        },
        layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => line(),
            vLineColor: () => line(),
            paddingTop: () => 0,
            paddingBottom: () => 0,
            paddingLeft: () => 0,
            paddingRight: () => 0,
        },
    };
}

/**
 * Mini grid «Допълнителен опит» (5 columns).
 * @param {Array<Record<string, *>>} items — rows with keys from DOSSIER_ADDITIONAL_EXPERIENCE_ROW_KEYS
 */
function buildAdditionalExperienceMiniTable(items, s) {
    const fs = Math.max(6, Number(s.cellFontSize) - 1);
    const hdr = s.childrenMiniHeaderFill;
    const line = () => s.borderColor;
    const pad = [2, 1, 2, 1];
    const keys = DOSSIER_ADDITIONAL_EXPERIENCE_ROW_KEYS;

    const headerCells = DOSSIER_ADDITIONAL_EXPERIENCE_HEADER_LABELS.map((text) => ({
        text,
        bold: true,
        fontSize: fs,
        fillColor: hdr,
        margin: pad,
    }));

    const list = Array.isArray(items) && items.length > 0 ? items : [{}];
    const body = list.map((row) =>
        keys.map((key) => ({
            text: formatDossierScalar(row?.[key]),
            fontSize: fs,
            margin: pad,
        }))
    );

    return {
        table: {
            widths: [48, 48, '*', '*', 44],
            body: [headerCells, ...body],
        },
        layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => line(),
            vLineColor: () => line(),
            paddingTop: () => 0,
            paddingBottom: () => 0,
            paddingLeft: () => 0,
            paddingRight: () => 0,
        },
    };
}

function buildExperienceGridFullWidthRow(items, s) {
    const mini = buildAdditionalExperienceMiniTable(items, s);
    return [
        {
            colSpan: 2,
            border: [false, false, false, false],
            margin: [2, 2, 2, 2],
            table: mini.table,
            layout: mini.layout,
        },
        {},
    ];
}

/**
 * Mini grid «Образование» under «Предишен опит» (title row + 6 columns).
 * @param {Array<Record<string, *>>} items — rows with keys from DOSSIER_EDUCATION_ROW_KEYS
 */
function buildEducationMiniTable(items, s) {
    const fs = Math.max(6, Number(s.cellFontSize) - 1);
    const hdr = s.childrenMiniHeaderFill;
    const line = () => s.borderColor;
    const pad = [2, 1, 2, 1];
    const keys = DOSSIER_EDUCATION_ROW_KEYS;
    const colCount = keys.length;

    const titleRow = [
        {
            text: 'Образование',
            colSpan: colCount,
            bold: true,
            fontSize: fs,
            fillColor: hdr,
            margin: [4, 2, 4, 2],
        },
        ...Array.from({ length: colCount - 1 }, () => ({})),
    ];

    const headerCells = DOSSIER_EDUCATION_HEADER_LABELS.map((text) => ({
        text,
        bold: true,
        fontSize: fs,
        fillColor: hdr,
        margin: pad,
    }));

    const list = Array.isArray(items) && items.length > 0 ? items : [{}];
    const body = list.map((row) =>
        keys.map((key) => ({
            text: formatDossierScalar(row?.[key]),
            fontSize: fs,
            margin: pad,
        }))
    );

    return {
        table: {
            widths: [54, 44, '*', 40, '*', 38],
            body: [titleRow, headerCells, ...body],
        },
        layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => line(),
            vLineColor: () => line(),
            paddingTop: () => 0,
            paddingBottom: () => 0,
            paddingLeft: () => 0,
            paddingRight: () => 0,
        },
    };
}

function buildEducationGridFullWidthRow(items, s) {
    const mini = buildEducationMiniTable(items, s);
    return [
        {
            colSpan: 2,
            border: [false, false, false, false],
            margin: [2, 2, 2, 2],
            table: mini.table,
            layout: mini.layout,
        },
        {},
    ];
}

/**
 * Mini grid «Езици» (title row + 6 columns).
 * @param {Array<Record<string, *>>} items — rows with keys from DOSSIER_LANGUAGE_ROW_KEYS
 */
function buildLanguagesMiniTable(items, s) {
    const fs = Math.max(6, Number(s.cellFontSize) - 1);
    const hdr = s.childrenMiniHeaderFill;
    const line = () => s.borderColor;
    const pad = [2, 1, 2, 1];
    const keys = DOSSIER_LANGUAGE_ROW_KEYS;
    const colCount = keys.length;

    const titleRow = [
        {
            text: 'Езици',
            colSpan: colCount,
            bold: true,
            fontSize: fs,
            fillColor: hdr,
            margin: [4, 2, 4, 2],
        },
        ...Array.from({ length: colCount - 1 }, () => ({})),
    ];

    const headerCells = DOSSIER_LANGUAGE_HEADER_LABELS.map((text) => ({
        text,
        bold: true,
        fontSize: fs,
        fillColor: hdr,
        margin: pad,
    }));

    const list = Array.isArray(items) && items.length > 0 ? items : [{}];
    const body = list.map((row) =>
        keys.map((key) => ({
            text: formatDossierScalar(row?.[key]),
            fontSize: fs,
            margin: pad,
        }))
    );

    return {
        table: {
            widths: ['*', 30, 30, 34, 30, 30],
            body: [titleRow, headerCells, ...body],
        },
        layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => line(),
            vLineColor: () => line(),
            paddingTop: () => 0,
            paddingBottom: () => 0,
            paddingLeft: () => 0,
            paddingRight: () => 0,
        },
    };
}

function buildLanguagesGridFullWidthRow(items, s) {
    const mini = buildLanguagesMiniTable(items, s);
    return [
        {
            colSpan: 2,
            border: [false, false, false, false],
            margin: [2, 2, 2, 2],
            table: mini.table,
            layout: mini.layout,
        },
        {},
    ];
}

/**
 * Mini grid «Възнаграждение» (title row + 7 columns).
 * @param {Array<Record<string, *>>} items — rows with keys from DOSSIER_REMUNERATION_ROW_KEYS
 */
function buildRemunerationMiniTable(items, s) {
    const fs = Math.max(6, Number(s.cellFontSize) - 1);
    const hdr = s.childrenMiniHeaderFill;
    const line = () => s.borderColor;
    const pad = [2, 1, 2, 1];
    const keys = DOSSIER_REMUNERATION_ROW_KEYS;
    const colCount = keys.length;

    const titleRow = [
        {
            text: 'Възнаграждение',
            colSpan: colCount,
            bold: true,
            fontSize: fs,
            fillColor: hdr,
            margin: [4, 2, 4, 2],
        },
        ...Array.from({ length: colCount - 1 }, () => ({})),
    ];

    const headerCells = DOSSIER_REMUNERATION_HEADER_LABELS.map((text) => ({
        text,
        bold: true,
        fontSize: fs,
        fillColor: hdr,
        margin: pad,
    }));

    const list = Array.isArray(items) && items.length > 0 ? items : [{}];
    const body = list.map((row) =>
        keys.map((key) => ({
            text: formatDossierScalar(row?.[key]),
            fontSize: fs,
            margin: pad,
        }))
    );

    return {
        table: {
            widths: [38, 42, 44, 44, 28, 34, '*'],
            body: [titleRow, headerCells, ...body],
        },
        layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => line(),
            vLineColor: () => line(),
            paddingTop: () => 0,
            paddingBottom: () => 0,
            paddingLeft: () => 0,
            paddingRight: () => 0,
        },
    };
}

function buildRemunerationGridFullWidthRow(items, s) {
    const mini = buildRemunerationMiniTable(items, s);
    return [
        {
            colSpan: 2,
            border: [false, false, false, false],
            margin: [2, 2, 2, 2],
            table: mini.table,
            layout: mini.layout,
        },
        {},
    ];
}

/**
 * @param {object} row
 * @param {string} row.label
 * @param {*} [row.value]
 * @param {{ displayedCount: string, items: object[] }} [row.childrenDetail]
 * @param {{ items: object[] }} [row.experienceGrid]
 * @param {{ items: object[] }} [row.educationGrid]
 * @param {{ items: object[] }} [row.remunerationGrid]
 * @param {{ items: object[] }} [row.languagesGrid]
 */
function dossierSectionRowToCells(row, s) {
    if (row.experienceGrid) {
        return buildExperienceGridFullWidthRow(row.experienceGrid.items ?? [], s);
    }
    if (row.remunerationGrid) {
        return buildRemunerationGridFullWidthRow(row.remunerationGrid.items ?? [], s);
    }
    if (row.educationGrid) {
        return buildEducationGridFullWidthRow(row.educationGrid.items ?? [], s);
    }
    if (row.languagesGrid) {
        return buildLanguagesGridFullWidthRow(row.languagesGrid.items ?? [], s);
    }

    const labelCell = {
        text: row.label,
        fontSize: s.cellFontSize,
        fillColor: s.labelFill,
        margin: [3, 1, 3, 1],
    };

    if (!row.childrenDetail) {
        const v = formatDossierScalar(row.value);
        return [
            labelCell,
            {
                text: v,
                fontSize: s.cellFontSize,
                margin: [3, 1, 3, 1],
            },
        ];
    }

    const { displayedCount, items } = row.childrenDetail;
    const valueStack = [
        {
            text: displayedCount,
            fontSize: s.cellFontSize,
            margin: [3, 1, 3, 0],
        },
    ];
    if (items?.length > 0) {
        valueStack.push(buildChildrenMiniTable(items, s));
    }

    return [
        labelCell,
        {
            stack: valueStack,
            margin: [0, 0, 0, 0],
        },
    ];
}

/**
 * Builds a bordered two-column block: section title row + label | value rows.
 * Reuse for «Лични данни», «Текущо положение», etc.
 * @param {string} sectionTitle
 * @param {Array<{ label: string, value?: * } | { label: string, childrenDetail: { displayedCount: string, items: object[] } } | { experienceGrid: { items: object[] } } | { remunerationGrid: { items: object[] } } | { educationGrid: { items: object[] } } | { languagesGrid: { items: object[] } }>} rows
 */
export function buildDossierSectionTable(sectionTitle, rows) {
    const s = DOSSIER_TABLE_STYLES;
    const headerRow = [
        {
            text: sectionTitle,
            colSpan: 2,
            bold: true,
            fontSize: s.sectionHeaderFontSize,
            fillColor: s.sectionHeaderFill,
            margin: [4, 2, 4, 2],
        },
        {},
    ];
    const dataRows = rows.map((row) => dossierSectionRowToCells(row, s));

    return {
        margin: [0, s.titleMarginBottom, 0, 0],
        table: {
            widths: [s.labelColWidth, '*'],
            body: [headerRow, ...dataRows],
        },
        layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => s.borderColor,
            vLineColor: () => s.borderColor,
            paddingTop: () => 0,
            paddingBottom: () => 1,
            paddingLeft: () => 0,
            paddingRight: () => 0,
        },
    };
}

function getPersonalDataRows(personal = {}) {
    const merged = { ...EMPTY_DOSSIER_PERSONAL_DATA, ...personal };
    const keys = DOSSIER_PERSONAL_DATA_FIELD_KEYS;
    const labels = DOSSIER_PERSONAL_DATA_LABELS;
    const rows = [];

    for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        rows.push({
            label: labels[i],
            value: merged[key],
        });

        if (key === 'maritalStatus') {
            const items = Array.isArray(merged.children) ? merged.children : [];
            const displayedCount =
                merged.childrenCount != null && merged.childrenCount !== ''
                    ? String(merged.childrenCount)
                    : String(items.length);

            rows.push({
                label: 'Брой деца:',
                childrenDetail: {
                    displayedCount,
                    items,
                },
            });
        }
    }

    return rows;
}

function getCurrentPositionRows(currentPosition = {}) {
    const merged = { ...EMPTY_DOSSIER_CURRENT_POSITION_DATA, ...currentPosition };
    return DOSSIER_CURRENT_POSITION_FIELD_KEYS.map((key, i) => ({
        label: DOSSIER_CURRENT_POSITION_LABELS[i],
        value: merged[key],
    }));
}

function getAdditionalExperienceRows(additionalExperience = {}) {
    const merged = { ...EMPTY_DOSSIER_ADDITIONAL_EXPERIENCE_DATA, ...additionalExperience };
    const experiences = Array.isArray(merged.experiences) ? merged.experiences : [];
    const remuneration = Array.isArray(merged.remuneration) ? merged.remuneration : [];

    const footerRows = DOSSIER_ADDITIONAL_EXPERIENCE_FOOTER_KEYS.map((key, i) => ({
        label: DOSSIER_ADDITIONAL_EXPERIENCE_FOOTER_LABELS[i],
        value: merged[key],
    }));

    return [
        { experienceGrid: { items: experiences } },
        { remunerationGrid: { items: remuneration } },
        ...footerRows,
    ];
}

function getPreviousExperienceRows(previousExperience = {}) {
    const merged = { ...EMPTY_DOSSIER_PREVIOUS_EXPERIENCE_DATA, ...previousExperience };
    const education = Array.isArray(merged.education) ? merged.education : [];
    const languages = Array.isArray(merged.languages) ? merged.languages : [];

    return [
        { educationGrid: { items: education } },
        { languagesGrid: { items: languages } },
    ];
}

/**
 * Dossier PDF: logo, title «Д О С И Е», section tables (starts with «Лични данни»). Opens in a new tab.
 * @param {Object} [options]
 * @param {string} [options.logoPath] — defaults to DOSSIER_LOGO_PATH
 * @param {string} [options.title] — visible title (spaces preserved), default «Д О С И Е»
 * @param {Object} [options.dossierData]
 * @param {Object} [options.dossierData.personal] — from `/emp/file/{id}` via mapEmpFileToDossierData
 * @param {Object} [options.dossierData.additionalExperience] — `experiences[]` rows keyed like DOSSIER_ADDITIONAL_EXPERIENCE_ROW_KEYS plus footer keys from DOSSIER_ADDITIONAL_EXPERIENCE_FOOTER_KEYS
 * @param {Object} [options.dossierData.previousExperience] — `education[]` rows keyed like DOSSIER_EDUCATION_ROW_KEYS plus `languages`
 */
export async function generateDossierPreviewPdf(options = {}) {
    const logoPath = options.logoPath ?? DOSSIER_LOGO_PATH;
    const titleText = options.title ?? 'Д О С И Е';
    const personalRows = getPersonalDataRows(options.dossierData?.personal);
    const currentPositionRows = getCurrentPositionRows(options.dossierData?.currentPosition);
    const additionalExperienceRows = getAdditionalExperienceRows(options.dossierData?.additionalExperience);
    const previousExperienceRows = getPreviousExperienceRows(options.dossierData?.previousExperience);

    let logoData;
    if (logoPath.startsWith('data:')) {
        logoData = logoPath;
    } else {
        const url = logoPath.startsWith('http')
            ? logoPath
            : `${window.location.origin}${logoPath.startsWith('/') ? '' : '/'}${logoPath}`;
        logoData = await fetchImageAsDataUrl(url);
    }

    const photoAsset = await fetchApiImageAsDataUrl(options.dossierData?.photoUrl);

    const dossierStyles = DOSSIER_TABLE_STYLES;
    let photoColumn = {
        width: dossierStyles.employeePhotoMaxWidth,
        text: '',
    };

    if (photoAsset?.dataUrl) {
        const fitted = scaleImageToFit(
            photoAsset.width,
            photoAsset.height,
            dossierStyles.employeePhotoMaxWidth,
            dossierStyles.employeePhotoMaxHeight
        );

        photoColumn = {
            width: fitted.width,
            stack: [
                {
                    image: photoAsset.dataUrl,
                    width: fitted.width,
                    height: fitted.height,
                    alignment: 'right',
                },
            ],
            margin: [0, 2, 0, 0],
        };
    }
    const docDefinition = {
        pageSize: 'A4',
        pageOrientation: 'portrait',
        pageMargins: [30, 30, 30, 34],
        info: {
            title: titleText,
        },
        content: [
            {
                columns: [
                    {
                        image: logoData,
                        width: 120,
                        margin: [0, 0, 0, 0],
                    },
                    {
                        width: '*',
                        text: titleText,
                        alignment: 'center',
                        bold: true,
                        fontSize: dossierStyles.documentTitleFontSize,
                        margin: [0, dossierStyles.documentTitleTopMargin, 0, 0],
                    },
                    photoColumn,
                ],
            },
            buildDossierSectionTable('Лични данни', personalRows),
            buildDossierSectionTable('Текущо положение', currentPositionRows),
            buildDossierSectionTable('Допълнителен опит', additionalExperienceRows),
            buildDossierSectionTable('Предишен опит', previousExperienceRows),
        ],
        defaultStyle: {
            font: 'Roboto',
            fontSize: dossierStyles.cellFontSize,
        },
    };

    const pdfDoc = pdfMake.createPdf(docDefinition);
    pdfDoc.getBlob((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
    });
}
